const useResizeObserver = typeof window["ResizeObserverEntry"] == "function"

export interface PopupContentProps<Result = any> {
    close: (result?: Result) => void
}

export type PopupOrigin = keyof (typeof Origins)

export type PopupAlignment = keyof (typeof Alignments)


interface PopupProps<Result> {

    popupContent: React.FunctionComponent<any>
    popupContentProps?: () => any
    popupCssClass?: string
    unstyled?: boolean

    onVisibilityChanged?: (visible: boolean) => void
    onClose?: (result?: Result) => void

    align?: PopupAlignment
    origin?: PopupOrigin

    disabled?: boolean
    wrapper?: boolean
    outsideClick?: boolean

}

const Origins = {
    cursor: undefined as ReturnType<typeof getOriginFunction>,
    bottomCenter: getOriginFunction(0.5, 0, 1, 0),
    bottomLeft: getOriginFunction(0, 0, 1, 0)
}

function getOriginFunction(xscale = 0, xoffset = 0, yscale = 0, yoffset = 0) {
    return (rect: ClientRect) => {
        const x = rect.left + (rect.width * xscale) + xoffset
        const y = rect.top + (rect.height * yscale) + yoffset
        return { x, y }
    }
}

const Alignments = {
    topLeft: [0, 0],
    topRight: [-1, 0],
    bottomRight: [-1, -1],
    bottomLeft: [0, -1]
}

export function Popup<Result = any>
    (
        {
            children,
            disabled,
            popupContent,
            popupContentProps,
            popupCssClass,
            align,
            origin,
            onVisibilityChanged,
            onClose,
            wrapper,
            outsideClick,
            unstyled
        }: React.PropsWithChildren<PopupProps<Result>>
    ) {

    const [state, setState] = React.useState({ visible: false, x: 0, y: 0, clientRect: undefined as ClientRect })
    const { visible, x, y, clientRect } = state

    let portal: React.ReactPortal
    let popupContentRef = React.useRef<HTMLDivElement>()
    let ref = React.useRef<HTMLDivElement>()

    const toggle = (event: React.MouseEvent) => {

        const clientRect: DOMRect = ref.current?.getBoundingClientRect()

        setState({
            visible: !visible,
            x: event.clientX,
            y: event.clientY,
            clientRect,
        })

        if (isFunction(onVisibilityChanged)) {
            onVisibilityChanged(!visible)
        }

    }


    React.useEffect(() => {

        if (visible && (outsideClick !== false)) {

            document.addEventListener("click", handleOutsideClick, true)

            return () => {
                document.removeEventListener("click", handleOutsideClick, true)
            }

        }

        function handleOutsideClick(event: PointerEvent) {

            if (visible && popupContentRef.current && ref.current) {

                const target = event.target as Element

                if (ref.current.contains(target)) {
                    return
                }

                if (popupContentRef.current.contains(target)) {
                    return
                }

                setState({
                    ...state,
                    visible: false
                })

                if (isFunction(onVisibilityChanged)) {
                    onVisibilityChanged(false)
                }

            }
        }

    })

    if (disabled) {
        return <>{children}</>
    }

    if (visible) {

        const close = (result?: Result) => {

            setState({ ...state, visible: false })

            if (isFunction(onClose)) {
                onClose(result)
            }

        }


        const content = <PopupContentSize {...{ x, y, clientRect, contentRef: popupContentRef, close, popupContent, popupContentProps, popupCssClass, align, origin, unstyled }}>
        </PopupContentSize>

        portal = ReactDOM.createPortal(content, document.body)

    }

    if (wrapper === false) {

        React.Children.only(children)

        return <>
            {React.Children.map(children, (c: any) => React.cloneElement(c, {
                onClick: (e: React.MouseEvent) => toggle(e),
                ref
            }))}
            {portal}
        </>

    }

    return <>
        <div ref={ref} style={{ display: "inline-block" }} onClick={toggle}>
            {children}
        </div>
        {portal}
    </>

}

interface PopupContentSizeProps {

    x: number,
    y: number,
    clientRect: ClientRect,
    contentRef: React.MutableRefObject<HTMLDivElement>

    align?: PopupAlignment,
    origin?: PopupOrigin,

    close: (result?: any) => void,

    popupContent: React.FunctionComponent<PopupContentProps>
    popupContentProps: () => any
    popupCssClass?: string,
    unstyled?: boolean

}

function PopupContentSize({ x, y, clientRect, contentRef, close, popupContent, popupContentProps, popupCssClass, unstyled = false, origin = "bottomCenter", align = "topLeft" }: React.PropsWithChildren<PopupContentSizeProps>) {

    const [state, setState] = React.useState({ popupWidth: 0, popupHeight: 0 })

    const { popupWidth, popupHeight } = state

    if (useResizeObserver) {

        const observer = React.useRef(

            new ResizeObserver((entries: ResizeObserverEntry[]) => {

                const e = entries[0]

                if (e) {

                    const { offsetWidth: w, offsetHeight: h } = (e.target as HTMLElement)

                    if ((w != popupWidth) || (h != popupHeight)) {
                        setState({ popupWidth: w, popupHeight: h })
                    }

                }

            })

        )

        React.useEffect(() => {

            const elt = contentRef.current

            if (elt) {
                observer.current.observe(elt)
            }

            return () => observer.current.unobserve(elt)


        }, [contentRef.current])

    }
    else {

        React.useLayoutEffect(() => {

            const div = contentRef.current as HTMLElement
            const w = div.offsetWidth
            const h = div.offsetHeight

            if ((w != popupWidth) || (h != popupHeight)) {
                setState({ popupWidth: w, popupHeight: h })
            }

        })

    }

    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
    const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)

    const or = Origins[origin]

    if (isFunction(or) && clientRect) {
        ({ x, y } = or(clientRect))
    }

    const al = Alignments[align]

    x = x + (popupWidth * al[0])
    y = y + (popupHeight * al[1])

    if ((x + popupWidth) > vw) x -= ((x + popupWidth) - vw)
    if ((y + popupHeight) > vh) y -= ((y + popupHeight) - vh)

    x = Math.max(x, 0)
    y = Math.max(y, 0)

    const props = {
        ...(isFunction(popupContentProps) ? popupContentProps() : {}),
        close
    }

    const style: React.CSSProperties = {
        position: "absolute",
        left: x + "px",
        top: y + "px",
        display: "block",
        zIndex: 2000
    }

    if (unstyled) {

        return <div
            ref={contentRef}
            className={popupCssClass}
            style={style}>

            {popupContent(props)}

        </div>

    }

    return <div
        ref={contentRef}
        className={`popover ${popupCssClass}`}
        style={style}>

        <div className="popover-inner">
            <div className="popover-content">
                {popupContent(props)}
            </div>
        </div>

    </div>

}

function isFunction(arg: any): arg is Function {
    return (typeof arg === "function")
}
